'use client';

import { useMemo, useState, useEffect, useRef } from 'react';

const HOLDINGS_FILTERS_STORAGE_KEY = 'holdingsFilters';

export const useHoldingsFilters = (holdings) => {
    const categoryTypes = useMemo(() => {
        const uniqueCategories = [...new Set(holdings.map((h) => h.category))];
        return uniqueCategories.map((value, id) => ({
            id,
            value,
            label:
                value === 'Holding'
                    ? 'Holdings'
                    : value?.charAt(0).toUpperCase() + value?.slice(1),
        }));
    }, [holdings]);

    const sectorTypes = useMemo(() => {
        const uniqueSectors = [
            ...new Set(holdings.map((h) => h.sector).filter(Boolean)),
        ];
        return uniqueSectors.map((value, id) => ({
            id,
            value,
            label: value?.charAt(0).toUpperCase() + value?.slice(1),
        }));
    }, [holdings]);

    const guoTypes = useMemo(() => {
        return [
            ...new Set(
                holdings
                    .map((h) => h.globalUltimateOwner?.trim())
                    .filter(Boolean)
            ),
        ].map((value, id) => ({
            id,
            value,
            label: value,
        }));
    }, [holdings]);

    const sortedByRevenue = [...holdings]
        .filter((h) => !isNaN(+h.revenue))
        .sort((a, b) => +b.revenue - +a.revenue);

    const highestRevenue =
        sortedByRevenue.length > 0 ? +sortedByRevenue[0].revenue : 0;
    const highestEmployee = Math.max(
        ...holdings.map((h) => +h.employeeCount || 0),
        0
    );

    const getDefaultState = () => ({
        searchTerm: '',
        selectedCategories: {},
        selectedGUOs: {},
        selectedSectors: {},
        revenueRange: highestRevenue > 0 ? [0, highestRevenue] : [0, 0],
        employeeRange: highestEmployee > 0 ? [0, highestEmployee] : [0, 0],
    });

    const readFromStorage = () => {
        const defaults = getDefaultState();
        if (typeof window === 'undefined') return defaults;

        try {
            const raw = sessionStorage.getItem(HOLDINGS_FILTERS_STORAGE_KEY);
            if (!raw) return defaults;
            const parsed = JSON.parse(raw) || {};

            const parseRange = (range, max) => {
                if (!Array.isArray(range)) return max > 0 ? [0, max] : [0, 0];
                const minRaw = Number(range[0]);
                const maxRaw = Number(range[1]);
                const min = Number.isFinite(minRaw) ? minRaw : 0;
                const upper = Number.isFinite(maxRaw)
                    ? maxRaw
                    : max > 0
                      ? max
                      : 0;

                if (max <= 0) {
                    return [Math.max(0, min), Math.max(0, upper)];
                }

                if (min === 0 && upper === 0) return [0, max];

                const clampedMin = Math.max(0, Math.min(min, max));
                const clampedMax = Math.max(clampedMin, Math.min(upper, max));
                return [clampedMin, clampedMax];
            };

            return {
                searchTerm:
                    typeof parsed.searchTerm === 'string'
                        ? parsed.searchTerm
                        : defaults.searchTerm,
                selectedCategories:
                    parsed.selectedCategories &&
                    typeof parsed.selectedCategories === 'object'
                        ? parsed.selectedCategories
                        : defaults.selectedCategories,
                selectedGUOs:
                    parsed.selectedGUOs &&
                    typeof parsed.selectedGUOs === 'object'
                        ? parsed.selectedGUOs
                        : defaults.selectedGUOs,
                selectedSectors:
                    parsed.selectedSectors &&
                    typeof parsed.selectedSectors === 'object'
                        ? parsed.selectedSectors
                        : defaults.selectedSectors,
                revenueRange: parseRange(parsed.revenueRange, highestRevenue),
                employeeRange: parseRange(
                    parsed.employeeRange,
                    highestEmployee
                ),
            };
        } catch (e) {
            console.warn('Kunde inte parsa sparade holdings-filter:', e);
            return defaults;
        }
    };

    const writeToStorage = (state) => {
        if (typeof window === 'undefined') return;
        try {
            sessionStorage.setItem(
                HOLDINGS_FILTERS_STORAGE_KEY,
                JSON.stringify(state)
            );
        } catch (e) {
            console.warn(
                'Kunde inte spara holdings-filter till sessionStorage:',
                e
            );
        }
    };

    const initial = readFromStorage();

    const [searchTerm, setSearchTerm] = useState(initial.searchTerm);
    const [selectedCategories, setSelectedCategories] = useState(
        initial.selectedCategories
    );
    const [selectedGUOs, setSelectedGUOs] = useState(initial.selectedGUOs);
    const [selectedSectors, setSelectedSectors] = useState(
        initial.selectedSectors
    );
    const [revenueRange, setRevenueRange] = useState(initial.revenueRange);
    const [employeeRange, setEmployeeRange] = useState(initial.employeeRange);

    const didSyncOnce = useRef(false);
    useEffect(() => {
        writeToStorage({
            searchTerm,
            selectedCategories,
            selectedGUOs,
            selectedSectors,
            revenueRange,
            employeeRange,
        });
        didSyncOnce.current = true;
    }, []);

    useEffect(() => {
        if (highestRevenue <= 0) return;
        setRevenueRange((prev) => {
            const min = Number.isFinite(+prev?.[0]) ? +prev[0] : 0;
            const maxVal = Number.isFinite(+prev?.[1])
                ? +prev[1]
                : highestRevenue;

            if (min === 0 && maxVal === 0) return [0, highestRevenue];

            const nextMin = Math.max(0, Math.min(min, highestRevenue));
            const nextMax = Math.max(nextMin, Math.min(maxVal, highestRevenue));
            return [nextMin, nextMax];
        });
    }, [highestRevenue]);

    useEffect(() => {
        if (highestEmployee <= 0) return;
        setEmployeeRange((prev) => {
            const min = Number.isFinite(+prev?.[0]) ? +prev[0] : 0;
            const maxVal = Number.isFinite(+prev?.[1])
                ? +prev[1]
                : highestEmployee;

            if (min === 0 && maxVal === 0) return [0, highestEmployee];

            const nextMin = Math.max(0, Math.min(min, highestEmployee));
            const nextMax = Math.max(
                nextMin,
                Math.min(maxVal, highestEmployee)
            );
            return [nextMin, nextMax];
        });
    }, [highestEmployee]);

    useEffect(() => {
        if (!didSyncOnce.current) return;
        writeToStorage({
            searchTerm,
            selectedCategories,
            selectedGUOs,
            selectedSectors,
            revenueRange,
            employeeRange,
        });
    }, [
        searchTerm,
        selectedCategories,
        selectedSectors,
        selectedGUOs,
        revenueRange,
        employeeRange,
    ]);

    const isFiltering = useMemo(() => {
        const hasSelectedCategory =
            Object.values(selectedCategories).some(Boolean);
        const hasSelectedGuo = Object.values(selectedGUOs).some(Boolean);
        const hasSelectedSector = Object.values(selectedSectors).some(Boolean);
        const isRevenueChanged =
            revenueRange[0] > 0 || revenueRange[1] < highestRevenue;
        const isEmployeeChanged =
            employeeRange[0] > 0 || employeeRange[1] < highestEmployee;
        const hasSearch = searchTerm.length > 0;

        return (
            hasSelectedCategory ||
            hasSelectedGuo ||
            hasSelectedSector ||
            isRevenueChanged ||
            isEmployeeChanged ||
            hasSearch
        );
    }, [
        selectedCategories,
        selectedGUOs,
        selectedSectors,
        revenueRange,
        employeeRange,
        highestRevenue,
        highestEmployee,
        searchTerm,
    ]);

    const filteredHoldings = useMemo(() => {
        if (!isFiltering) return holdings;

        const hasEmployeeFilter =
            employeeRange[0] > 0 || employeeRange[1] < highestEmployee;

        return holdings.filter((h) => {
            const matchSearch =
                !searchTerm ||
                h.name?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchCategory =
                !Object.values(selectedCategories).some(Boolean) ||
                selectedCategories[h.category];

            const matchGuo =
                !Object.values(selectedGUOs).some(Boolean) ||
                Object.keys(selectedGUOs).some(
                    (key) =>
                        selectedGUOs[key] &&
                        key.trim().toLowerCase() ===
                            h.globalUltimateOwner?.trim().toLowerCase()
                );

            const matchSector =
                !Object.values(selectedSectors).some(Boolean) ||
                selectedSectors[h.sector];

            const isFilteringRevenue =
                revenueRange[0] > 0 || revenueRange[1] < highestRevenue;

            const matchRevenue = !isFilteringRevenue
                ? true
                : h.revenue != null &&
                  !isNaN(+h.revenue) &&
                  +h.revenue >= revenueRange[0] &&
                  +h.revenue <= revenueRange[1];

            const matchEmployees =
                !hasEmployeeFilter ||
                (h.employeeCount != null &&
                    +h.employeeCount >= employeeRange[0] &&
                    +h.employeeCount <= employeeRange[1]);

            return (
                matchSearch &&
                matchCategory &&
                matchGuo &&
                matchRevenue &&
                matchSector &&
                matchEmployees
            );
        });
    }, [
        holdings,
        selectedCategories,
        selectedGUOs,
        selectedSectors,
        revenueRange,
        employeeRange,
        searchTerm,
        isFiltering,
        highestEmployee,
        highestRevenue,
    ]);

    const activeFilters = useMemo(() => {
        let count = 0;

        if (searchTerm.length > 0) count++;
        count += Object.values(selectedCategories).filter(Boolean).length;
        count += Object.values(selectedSectors).filter(Boolean).length;
        count += Object.values(selectedGUOs).filter(Boolean).length;

        const isRevenueChanged =
            revenueRange[0] > 0 || revenueRange[1] < highestRevenue;
        const isEmployeeChanged =
            employeeRange[0] > 0 || employeeRange[1] < highestEmployee;

        if (isRevenueChanged) count++;
        if (isEmployeeChanged) count++;

        return count;
    }, [
        searchTerm,
        selectedCategories,
        selectedSectors,
        selectedGUOs,
        revenueRange,
        employeeRange,
        highestRevenue,
        highestEmployee,
    ]);

    const clearFilters = () => {
        const defaults = getDefaultState();
        setSelectedCategories(defaults.selectedCategories);
        setSelectedSectors(defaults.selectedSectors);
        setSelectedGUOs(defaults.selectedGUOs);
        setSearchTerm(defaults.searchTerm);
        setRevenueRange(defaults.revenueRange);
        setEmployeeRange(defaults.employeeRange);

        if (typeof window !== 'undefined') {
            try {
                sessionStorage.removeItem(HOLDINGS_FILTERS_STORAGE_KEY);
            } catch (e) {
                console.warn(
                    'Kunde inte ta bort holdings-filter från sessionStorage:',
                    e
                );
            }
        }
    };

    return {
        categoryTypes,
        guoTypes,
        sectorTypes,
        searchTerm,
        setSearchTerm,
        selectedCategories,
        setSelectedCategories,
        selectedGUOs,
        setSelectedGUOs,
        selectedSectors,
        setSelectedSectors,
        revenueRange,
        setRevenueRange,
        employeeRange,
        setEmployeeRange,
        filteredHoldings,
        highestRevenue,
        highestEmployee,
        isFiltering,
        clearFilters,
        activeFilters,
    };
};
